// Mock app data for the authenticated experience.
// Replaced by live FastAPI + MongoDB responses in the backend phase.

export const dailyQuests = [
  { id: "q1", label: "Log a workout", points: 50, done: true },
  { id: "q2", label: "Drink 2L of water", points: 30, done: false },
  { id: "q3", label: "Check in before noon", points: 20, done: false },
];

export const todayStats = {
  waterMl: 1400,
  waterGoalMl: 2500,
  weightKg: 68.4,
  weightTrend: -0.6,
  caloriesIn: 1620,
  caloriesGoal: 2100,
  protein: 96,
  proteinGoal: 130,
  carbs: 180,
  fat: 52,
  activeMinutes: 42,
};

export const coaches = [
  {
    id: "amara",
    name: "Amara",
    role: "Warm & encouraging",
    accent: "#6AA6FF",
    tagline: "Celebrates every win with you.",
    intro: "Hi Daniel \u2014 I'm so proud of your 23-day streak. What are we celebrating today?",
    replies: [
      "That's beautiful progress \u2014 your consistency is genuinely inspiring. Let's keep this gentle momentum going.",
      "I love that. Every small choice compounds. How did that make you feel afterwards?",
      "You showed up, and that's the whole game. Proud of you. Want a light recovery idea for tomorrow?",
    ],
  },
  {
    id: "jabari",
    name: "Jabari",
    role: "Tough love",
    accent: "#D4924A",
    tagline: "No excuses. Only results.",
    intro: "Daniel. 23 days in. Don't get comfortable \u2014 comfortable is where progress dies. What's the plan today?",
    replies: [
      "Good. Now do it again tomorrow. Discipline beats motivation every single time.",
      "No shortcuts. Add one more set and own it. You've got more in the tank.",
      "Results talk. Log it, then let's raise the bar next session.",
    ],
  },
  {
    id: "zuri",
    name: "Zuri",
    role: "Wellness & recovery",
    accent: "#7A9E7E",
    tagline: "Balances body and mind.",
    intro: "Hey Daniel. Before we push \u2014 how's your sleep and stress? Recovery is where the growth happens.",
    replies: [
      "Let's protect your recovery today. Try 10 minutes of slow breathing before bed \u2014 it changes everything.",
      "Your body is asking for rest, and that's wisdom, not weakness. Honor it.",
      "Beautiful. Pair movement with stillness and you'll feel unstoppable and calm.",
    ],
  },
  {
    id: "kofi",
    name: "Kofi",
    role: "Strategic & data-driven",
    accent: "#A9CCFF",
    tagline: "Reads your data, spots plateaus.",
    intro: "Daniel \u2014 your weight trend is down 0.6kg and protein is under target. Let's build a smarter week.",
    replies: [
      "Based on your last 7 days, bump protein by 30g and you'll see recovery improve measurably.",
      "Your volume is trending up but rest is flat. Let's schedule a deload before a plateau hits.",
      "Data says mornings are your strongest window. Let's anchor key sessions there.",
    ],
  },
  {
    id: "lumi",
    name: "Lumi",
    role: "Cycle, pregnancy & postpartum care",
    accent: "#E7A6C4",
    tagline: "Gentle, intentional support for every stage.",
    intro: "Hi love \u2014 I'm Lumi. I'm here for your cycle, your pregnancy and the fourth trimester. How are you feeling today, really?",
    replies: [
      "Whatever you're feeling is valid, and you don't have to carry it alone. Be gentle with yourself today. \ud83d\udc9b",
      "Rest isn't lazy right now \u2014 it's how your body does its quiet, incredible work. Keep water and a snack close.",
      "You're doing so much better than you think. If anything ever feels off or heavy, let's gently loop in your doctor or midwife.",
    ],
  },
];

// A returning "welcome back" line spoken in the member's chosen coach's voice.
// ctx: { name, days (away), streak, updates (feed count) }
export function coachWelcomeLine(coachId, ctx = {}) {
  const name = ctx.name || "there";
  const days = ctx.days || 0;
  const streak = ctx.streak || 0;
  const updates = ctx.updates || 0;
  const V = {
    amara: {
      away: `Welcome back, ${name} — I've missed you. ${days} day${days === 1 ? "" : "s"} away is nothing; let's ease back in together. 💙`,
      streak: `There you are, ${name}. Your ${streak}-day streak has been waiting — so proud of you for showing up again.`,
      updates: `Welcome back, ${name}. Your weave has been busy — ${updates} lovely moment${updates === 1 ? "" : "s"} to celebrate.`,
      base: `So glad you're here, ${name}. Let's make today gentle and strong.`,
    },
    jabari: {
      away: `${days} day${days === 1 ? "" : "s"} off, ${name}. Rest's over — let's get back to work.`,
      streak: `Back at it, ${name}. ${streak} days strong. Don't you dare break it now.`,
      updates: `${name}. Your weave moved ${updates} time${updates === 1 ? "" : "s"} while you sat out. Your turn.`,
      base: `No warm-up talk, ${name}. Let's earn today.`,
    },
    zuri: {
      away: `Welcome back, ${name}. ${days} day${days === 1 ? "" : "s"} of rest — now let's rebuild gently and mindfully.`,
      streak: `Good to see you, ${name}. ${streak} days in — let's honour that with a balanced day.`,
      updates: `Welcome back, ${name}. Breathe in — ${updates} update${updates === 1 ? "" : "s"} from your circle await.`,
      base: `Ease in, ${name}. Let's check in with body and mind first.`,
    },
    kofi: {
      away: `Welcome back, ${name}. ${days} day${days === 1 ? "" : "s"} away — smart plan today beats a hard one. Let's map it.`,
      streak: `${name}, ${streak} days logged. The data loves you. Let's keep the trend climbing.`,
      updates: `Welcome back, ${name}. ${updates} new signal${updates === 1 ? "" : "s"} in your weave — let's read the room and move.`,
      base: `Right, ${name} — let's be intentional today. One clear target.`,
    },
    lumi: {
      away: `Welcome back, ${name} ✨ ${days} day${days === 1 ? "" : "s"} away, and the light's still on for you. Let's glow today.`,
      streak: `Hey ${name} ✨ ${streak} days of showing up — that's your spark. Let's keep it lit.`,
      updates: `Welcome back, ${name} ✨ ${updates} little spark${updates === 1 ? "" : "s"} from your weave while you were gone.`,
      base: `You're here, ${name} ✨ Let's make today feel good.`,
    },
  };
  const v = V[coachId] || V.amara;
  if (days >= 2) return v.away;
  if (streak > 0) return v.streak;
  if (updates > 0) return v.updates;
  return v.base;
}


export const recentActivity = [
  { id: "a1", name: "Sofia R.", flag: "\uD83C\uDDE7\uD83C\uDDF7", sport: "Run", color: "#6AA6FF", distance: "8.2 km", duration: "41:20", when: "2h ago" },
  { id: "a2", name: "Marcus D.", flag: "\uD83C\uDDFA\uD83C\uDDF8", sport: "Ride", color: "#7DB2FF", distance: "24.6 km", duration: "58:04", when: "4h ago" },
  { id: "a3", name: "Amina K.", flag: "\uD83C\uDDF0\uD83C\uDDEA", sport: "Yoga", color: "#7A9E7E", distance: "\u2014", duration: "30:00", when: "6h ago" },
];

export const exerciseLibrary = [
  "Bench Press", "Incline Bench Press", "Dumbbell Press", "Squat", "Front Squat",
  "Deadlift", "Romanian Deadlift", "Overhead Press", "Push Press", "Pull Up",
  "Chin Up", "Lat Pulldown", "Barbell Row", "Dumbbell Row", "Seated Row",
  "Bicep Curl", "Hammer Curl", "Tricep Dip", "Tricep Pushdown", "Skull Crusher",
  "Leg Press", "Leg Extension", "Leg Curl", "Calf Raise", "Hip Thrust",
  "Lunges", "Bulgarian Split Squat", "Step Up", "Goblet Squat", "Kettlebell Swing",
  "Plank", "Side Plank", "Crunch", "Hanging Leg Raise", "Russian Twist",
  "Mountain Climber", "Burpee", "Box Jump", "Jump Rope", "Battle Ropes",
  "Running", "Trail Running", "Sprints", "Cycling", "Spin Class", "Walking",
  "Hiking", "Rowing", "Swimming", "Elliptical", "Stair Climber",
  "Yoga", "Pilates", "HIIT", "CrossFit", "Boxing", "Kickboxing", "Stretching",
  "Foam Rolling", "Meditation", "Breathwork",
];

export const loggedSets = [
  { id: "s1", exercise: "Squat", sets: 4, reps: 8, weight: 70, unit: "kg" },
  { id: "s2", exercise: "Bench Press", sets: 4, reps: 10, weight: 45, unit: "kg" },
  { id: "s3", exercise: "Barbell Row", sets: 3, reps: 12, weight: 40, unit: "kg" },
];

export const foodQuickPicks = [
  { name: "Greek Yogurt", cal: 130, p: 17, c: 9, f: 4 },
  { name: "Grilled Chicken (150g)", cal: 248, p: 46, c: 0, f: 6 },
  { name: "Banana", cal: 105, p: 1, c: 27, f: 0 },
  { name: "Oatmeal", cal: 150, p: 5, c: 27, f: 3 },
  { name: "Salmon Fillet", cal: 280, p: 39, c: 0, f: 13 },
  { name: "Avocado (half)", cal: 120, p: 1, c: 6, f: 11 },
  { name: "Almonds (28g)", cal: 164, p: 6, c: 6, f: 14 },
  { name: "Brown Rice (1 cup)", cal: 216, p: 5, c: 45, f: 2 },
];

export const meals = [
  { id: "me1", type: "Breakfast", name: "Oatmeal + Berries", cal: 320, p: 12 },
  { id: "me2", type: "Lunch", name: "Chicken & Rice Bowl", cal: 540, p: 48 },
  { id: "me3", type: "Snack", name: "Greek Yogurt", cal: 130, p: 17 },
];

export const weightHistory = [
  { d: "Mon", kg: 69.2 }, { d: "Tue", kg: 69.0 }, { d: "Wed", kg: 68.9 },
  { d: "Thu", kg: 68.7 }, { d: "Fri", kg: 68.6 }, { d: "Sat", kg: 68.5 }, { d: "Sun", kg: 68.4 },
];

export const leaderboard = [
  { rank: 1, name: "Amina K.", flag: "\uD83C\uDDF0\uD83C\uDDEA", points: 3120, streak: 41 },
  { rank: 2, name: "Yuki T.", flag: "\uD83C\uDDEF\uD83C\uDDF5", points: 2980, streak: 38 },
  { rank: 3, name: "Marcus D.", flag: "\uD83C\uDDFA\uD83C\uDDF8", points: 2640, streak: 29 },
  { rank: 4, name: "Daniel", flag: "\uD83C\uDDFA\uD83C\uDDF8", points: 1840, streak: 23, me: true },
  { rank: 5, name: "Sofia R.", flag: "\uD83C\uDDE7\uD83C\uDDF7", points: 1610, streak: 18 },
  { rank: 6, name: "Liam O.", flag: "\uD83C\uDDEE\uD83C\uDDEA", points: 1420, streak: 15 },
];

export const motivationQuotes = [
  "Small steps, taken daily, become a life transformed.",
  "You don't have to be extreme, just consistent.",
  "The calm you seek is built one gentle choice at a time.",
  "Show up for yourself the way you'd show up for someone you love.",
];
